/*
 * MSP 建立流程：
 * 1. 輸入 MSP 名稱。
 * 2. 輸入 License Key 並按 Apply；父層呼叫 read /ents/preconfirm，
 *    回傳各 key 的 VALID/INVALID、point、dailyRate、remainingDays 與 sufficientCredits。
 *    只有 sufficientCredits 為 true 時可進入下一步。
 * 3. 使用最後一次 preconfirm 結果顯示預扣與 wallet 試算；按 Set Up MSP 時，
 *    父層呼叫 create /ents，送出名稱及所有 VALID 的正規化 License Key。
 */

import React, { useEffect, useMemo, useRef, useState } from "react";

import {
  Box,
  Checkbox,
  CircularProgress,
  IconButton,
  Skeleton,
  TextField,
  Typography,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";

import ModalContainer from "components/ModalContainer";
import { SolidButton } from "components/buttons/SolidButton";
import { OutlineButton } from "components/buttons/OutlineButton";
import { OutlineRectangleButton } from "components/buttons/OutlineRectangleButton";
import {
  MSP_DAILY_CREDITS,
  MSP_MINIMUM_CREDIT_DAYS,
} from "const/license";

import { ReactComponent as CloseIcon } from "assets/images/icons/icn_close.svg";
import { ReactComponent as AddIcon } from "assets/images/icons/btn_add.svg";
import { ReactComponent as BinIcon } from "assets/images/icons/icn_btn_bin.svg";
import { ReactComponent as CheckBoxDefault } from "assets/images/icons/checkbox_default.svg";
import { ReactComponent as CheckBoxFilled } from "assets/images/icons/checkbox_fiilled.svg";
import { ReactComponent as CheckBoxClear } from "assets/images/icons/checkbox_clear.svg";

const steps = ["MSP Name", "Redeem credits", "Confirm"];
const MSP_MINIMUM_CREDITS = MSP_DAILY_CREDITS * MSP_MINIMUM_CREDIT_DAYS;

const normalizeKey = (item) => item.segments.join("").toUpperCase();

const StepHeader = ({ step }) => (
  <Box sx={{ display: "flex", alignItems: "center", px: "20px", pt: "18px", pb: "16px" }}>
    {steps.map((label, index) => {
      const stepNumber = index + 1;
      const completed = step > stepNumber;
      const active = step === stepNumber;

      return (
        <React.Fragment key={label}>
          <Box sx={{ display: "flex", alignItems: "center", flexShrink: 0 }}>
            <Box
              sx={{
                width: "22px", height: "22px", borderRadius: "50%",
                bgcolor: completed ? "#14A83B" : active ? "#172D6B" : "#ECEEF3",
                color: active || completed ? "#FFFFFF" : "#A2A8B3",
                display: "flex", alignItems: "center", justifyContent: "center",
                fontSize: "11px", fontWeight: 700,
              }}
            >
              {completed ? "✓" : stepNumber}
            </Box>
            <Typography sx={{ ml: "8px", color: active ? "#172D6B" : completed ? "#667085" : "#858D9D", fontSize: "14px", fontWeight: 700, whiteSpace: "nowrap" }}>
              {label}
            </Typography>
          </Box>
          {index < steps.length - 1 && <Box sx={{ flex: 1, mx: "12px", height: "2px", bgcolor: completed ? "#53B36B" : "#E2E5EC" }} />}
        </React.Fragment>
      );
    })}
  </Box>
);

const ModalSetupMsp = ({
  openModal, mspName, step, licenseKeys, preconfirm, isPreconfirming, isCreating,
  isPreconfirmCurrent,
  onChangeMspName, onChangeStep, onAddLicenseKey, onChangeLicenseKey,
  onRemoveLicenseKeys, onApplyLicenseKeys, onCloseModal, onCreateMsp,
}) => {
  const theme = useTheme();
  const [selectedIds, setSelectedIds] = useState([]);
  const inputRefs = useRef({});

  useEffect(() => {
    if (!openModal) setSelectedIds([]);
  }, [openModal]);

  const resultByKey = useMemo(() => (
    (preconfirm?.licenseKeys || []).reduce((result, item) => ({ ...result, [item.id]: item }), {})
  ), [preconfirm]);
  const redeemedCredits = licenseKeys.reduce(
    (total, item) => {
      const result = resultByKey[normalizeKey(item)];
      return total + (result?.status === "VALID" ? Number(result.point || 0) : 0);
    }, 0,
  );
  // The setup gate is fixed at 25 credits/day for 60 days, regardless of
  // the current-period pre-deduction returned by preconfirm.
  const requiredCredits = MSP_MINIMUM_CREDITS;
  const progress = requiredCredits ? Math.min((redeemedCredits / requiredCredits) * 100, 100) : 0;
  const completeKeys = licenseKeys.length > 0 && licenseKeys.every((item) => normalizeKey(item).length === 20);
  // Creating an MSP requires at least 60 days of its 25-credit daily rate.
  // Keep the API eligibility check as a second validation layer.
  const hasMinimumCredits = redeemedCredits >= MSP_MINIMUM_CREDITS;
  const canContinue = isPreconfirmCurrent && hasMinimumCredits && Boolean(preconfirm?.sufficientCredits);
  const allSelected = licenseKeys.length > 0 && selectedIds.length === licenseKeys.length;

  const handlePaste = (event, item, segmentIndex) => {
    event.preventDefault();
    const pasted = event.clipboardData.getData("text").toUpperCase().replace(/[^A-Z0-9]/g, "");
    (pasted.match(/.{1,5}/g) || []).slice(0, 4 - segmentIndex).forEach((segment, index) => {
      onChangeLicenseKey(item.id, segmentIndex + index, segment);
    });
  };

  const handleKeyChange = (item, segmentIndex, value) => {
    const normalizedValue = value.toUpperCase().replace(/[^A-Z0-9]/g, "").slice(0, 5);

    onChangeLicenseKey(item.id, segmentIndex, normalizedValue);

    if (normalizedValue.length === 5 && segmentIndex < item.segments.length - 1) {
      inputRefs.current[`${item.id}-${segmentIndex + 1}`]?.focus();
    }
  };

  const renderStepOne = () => (
    <Box sx={{ px: "40px", pt: "30px" }}>
      <Typography sx={{ color: "primary.greyPrimary", fontSize: "14px", lineHeight: 1.5 }}>
        Once set up, you get an MSP Console that sits above your organizations: see the health of every organization at a glance, with centralized billing, disbursement, subscriptions and reports.
      </Typography>
      <Box sx={{ mt: "40px", display: "flex", alignItems: "center", gap: 2 }}>
        <Typography sx={{ width: "86px", fontSize: "14px", color: "primary.greyPrimary" }}>MSP Name</Typography>
        <TextField fullWidth autoFocus value={mspName} onChange={(event) => onChangeMspName(event.target.value)} inputProps={{ maxLength: 100 }} sx={{ "& .MuiOutlinedInput-root": { height: "36px", borderRadius: 1 } }} />
      </Box>
    </Box>
  );

  const renderStepTwo = () => (
    <Box sx={{ px: "26px", pt: "10px" }}>
      <Typography sx={{ fontSize: "14px", color: "primary.greyPrimary", mb: "18px" }}>
        The MSP requires {MSP_DAILY_CREDITS} credits per day. Please provide credit redeem keys below.
      </Typography>
      <Box sx={{ mb: "14px" }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", fontSize: "12px", mb: "6px" }}>
          <Typography variant="caption">Redeemed {redeemedCredits.toLocaleString()} credits</Typography>
          <Typography variant="caption" fontWeight={700}>Required {requiredCredits.toLocaleString()} credits</Typography>
        </Box>
        <Box sx={{ height: "6px", bgcolor: "#E2E5EC", borderRadius: "4px", overflow: "hidden" }}><Box sx={{ width: `${progress}%`, height: "100%", bgcolor: "#172D6B", transition: "width 150ms" }} /></Box>
        <Typography variant="caption" sx={{ display: "block", mt: "6px", color: "primary.greyPrimary" }}>
          {preconfirm ? `${isPreconfirmCurrent ? `Redeemed ${redeemedCredits.toLocaleString()} credits` : "License keys changed"} — ${isPreconfirmCurrent ? `at least ${MSP_MINIMUM_CREDITS.toLocaleString()} credits are required to create an MSP${canContinue ? "; ready to continue" : "."}` : "click Apply to validate the updated list."}` : "Apply License Keys to calculate the required credits."}
        </Typography>
      </Box>
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1 }}>
        <Typography sx={{ color: "primary.main", fontSize: "14px", fontWeight: 700 }}>License Key List</Typography>
        <Box sx={{ display: "flex", gap: 1 }}>
          <OutlineRectangleButton iconStart={<AddIcon />} height="32px" onClick={onAddLicenseKey}>Add</OutlineRectangleButton>
          {selectedIds.length > 0 && <OutlineRectangleButton iconStart={<BinIcon />} height="32px" onClick={() => { onRemoveLicenseKeys(selectedIds); setSelectedIds([]); }}>
            Delete
            <Box component="span" sx={{ minWidth: "24px", height: "20px", ml: "6px", px: "8px", display: "inline-flex", alignItems: "center", justifyContent: "center", borderRadius: "10px", bgcolor: "primary.main", color: "primary.white", fontSize: "12px", fontWeight: 500 }}>
              {selectedIds.length}
            </Box>
          </OutlineRectangleButton>}
        </Box>
      </Box>
      <Box
        sx={{
          height: "300px",
          overflowY: "auto",
          border: "1px solid",
          borderColor: "primary.borderGreyPrimary",
          borderRadius: 1,
          scrollbarWidth: "thin",
          scrollbarColor: `${theme.palette.primary.main} ${theme.palette.primary.greyThread}`,
          "&::-webkit-scrollbar": { width: 5 },
          "&::-webkit-scrollbar-thumb": {
            backgroundColor: theme.palette.primary.main,
            borderRadius: 8,
          },
          "&::-webkit-scrollbar-thumb:hover": {
            backgroundColor: theme.palette.primary.mainHover,
          },
          "&::-webkit-scrollbar-track": {
            backgroundColor: theme.palette.primary.greyThread,
            borderRadius: theme.shape.customBorderRadius[0],
          },
          "&::-webkit-scrollbar-button": { display: "none", width: 0, height: 0 },
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", minHeight: "40px", px: 1, borderBottom: "1px solid", borderColor: "primary.borderGreyPrimary" }}>
          <Checkbox size="small" icon={<CheckBoxDefault style={{ width: "24px", height: "24px" }} />} checkedIcon={<CheckBoxFilled style={{ width: "24px", height: "24px" }} />} indeterminateIcon={<CheckBoxClear style={{ width: "24px", height: "24px" }} />} checked={allSelected} indeterminate={selectedIds.length > 0 && !allSelected} onChange={(event) => setSelectedIds(event.target.checked ? licenseKeys.map((item) => item.id) : [])} />
          <Typography variant="caption" fontWeight={700}>License Key</Typography>
          {preconfirm && <Typography variant="caption" fontWeight={700} sx={{ ml: "auto", pr: 1 }}>Credits</Typography>}
        </Box>
        {licenseKeys.map((item, rowIndex) => {
          const result = resultByKey[normalizeKey(item)];
          return (
            <Box key={item.id} sx={{ display: "flex", alignItems: "center", minHeight: "58px", px: 1, bgcolor: rowIndex % 2 === 0 ? "#F6F6F6" : "transparent" }}>
              <Checkbox size="small" icon={<CheckBoxDefault style={{ width: "24px", height: "24px" }} />} checkedIcon={<CheckBoxFilled style={{ width: "24px", height: "24px" }} />} checked={selectedIds.includes(item.id)} onChange={() => setSelectedIds((previous) => previous.includes(item.id) ? previous.filter((id) => id !== item.id) : [...previous, item.id])} />
              <Box sx={{ display: "flex", alignItems: "center", gap: "5px", flex: 1 }}>
                {item.segments.map((segment, index) => (
                  <React.Fragment key={index}>
                    <TextField inputRef={(element) => { inputRefs.current[`${item.id}-${index}`] = element; }} value={segment} onChange={(event) => handleKeyChange(item, index, event.target.value)} onPaste={(event) => handlePaste(event, item, index)} inputProps={{ maxLength: 5, "aria-label": `License key segment ${index + 1}` }} sx={{ width: "84px", "& .MuiOutlinedInput-root": { height: "32px" }, "& input": { p: "4px", textAlign: "center", fontSize: "13px" } }} />
                    {index < item.segments.length - 1 && <Typography variant="caption">−</Typography>}
                  </React.Fragment>
                ))}
              </Box>
              {preconfirm && <><Typography variant="caption" fontWeight={700} sx={{ width: "70px", textAlign: "right", color: result?.status === "VALID" ? "#008514" : result ? "error.main" : "text.secondary" }}>
                {result ? (result.status === "VALID" ? `+${Number(result.point || 0).toLocaleString()}` : "Invalid") : null}
              </Typography></>}
            </Box>
          );
        })}
      </Box>
      <Box sx={{ mt: 2, px: "12px", py: "10px", borderLeft: "3px solid", borderColor: "primary.main", borderRadius: "4px", bgcolor: "rgba(21, 67, 137, 0.05)" }}><Typography variant="caption" sx={{ color: "primary.main" }}>Credit amounts are revealed only after you click <Box component="span" fontWeight={700}>Apply</Box> — the server validates each key and returns its value at that point.</Typography></Box>
    </Box>
  );

  const renderStepThree = () => {
    const preDeducted = Number(preconfirm.dailyRate || 0) * Number(preconfirm.remainingDays || 0);
    const validKeyCount = (preconfirm.licenseKeys || []).filter((item) => item.status === "VALID").length;
    const summaryRows = [["MSP license", `${preconfirm.rate} (flat)`], ["Redeemed from keys", `${redeemedCredits.toLocaleString()} credits (${validKeyCount} license ${validKeyCount === 1 ? "key" : "keys"})`], ["Pre-deducted now (current period)", `${preDeducted.toLocaleString()} credits`], ["Wallet balance after", `${(redeemedCredits - preDeducted).toLocaleString()} credits`]];
    return <Box sx={{ px: "40px", pt: "24px" }}><Typography sx={{ fontSize: "14px", color: "primary.greyPrimary", mb: 2 }}>This creates the NTT Managed Service layer and its MSP Wallet, and brings your organizations under management.</Typography><Box sx={{ border: "1px solid", borderColor: "primary.borderGreyPrimary", borderRadius: 1, px: "28px", py: "18px" }}>{summaryRows.map(([label, value]) => <Box key={label} sx={{ display: "flex", justifyContent: "space-between", gap: 2, py: "7px" }}><Typography fontSize="14px">{label}</Typography><Typography fontSize="14px" fontWeight={700}>{value}</Typography></Box>)}</Box></Box>;
  };

  const renderCreatingSkeleton = () => (
    <Box sx={{ minHeight: "400px", p: "4px 20px" }} aria-label="Creating MSP">
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", pb: "14px", borderBottom: "1px solid", borderColor: "primary.borderGreyPrimary" }}>
        <Skeleton variant="text" width="280px" height="32px" />
        <Skeleton variant="circular" width="24px" height="24px" />
      </Box>
      <Box sx={{ display: "flex", gap: 2, py: "22px" }}>
        {[1, 2, 3].map((item) => <React.Fragment key={item}><Skeleton variant="circular" width="22px" height="22px" /><Skeleton variant="text" width="90px" height="22px" /></React.Fragment>)}
      </Box>
      <Skeleton variant="text" width="65%" height="24px" sx={{ mb: 2 }} />
      <Box sx={{ border: "1px solid", borderColor: "primary.borderGreyPrimary", borderRadius: 1, p: "18px 28px" }}>
        {[1, 2, 3, 4].map((item) => <Box key={item} sx={{ display: "flex", justifyContent: "space-between", py: "8px" }}><Skeleton variant="text" width="35%" height="22px" /><Skeleton variant="text" width="28%" height="22px" /></Box>)}
      </Box>
      <Box sx={{ display: "flex", justifyContent: "center", gap: 2, mt: "30px" }}>
        <Skeleton variant="rounded" width="100px" height="32px" />
        <Skeleton variant="rounded" width="120px" height="32px" />
      </Box>
    </Box>
  );

  return (
    <ModalContainer display={openModal} modalWidth="720px">
      {isCreating ? renderCreatingSkeleton() : <Box sx={{ position: "relative", p: 0 }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", borderBottom: "1px solid", borderColor: theme.palette.primary.borderGreyPrimary, mx: "-20px", px: "20px", pb: "10px" }}><Typography sx={{ color: theme.palette.primary.main, fontSize: theme.typography.customFontSize[5], fontWeight: theme.typography.customFontWeight[3] }}>Set Up MSP Central Management</Typography><IconButton aria-label="Close MSP setup dialog" onClick={onCloseModal} size="small"><CloseIcon /></IconButton></Box>
        <StepHeader step={step} />
        {step === 1 ? renderStepOne() : step === 2 ? renderStepTwo() : renderStepThree()}
        <Box sx={{ mt: "30px", mb: "20px", display: "flex", justifyContent: "center", gap: "20px" }}>
          {step === 1 && <><OutlineButton onClick={onCloseModal} width="100px" height="32px">Cancel</OutlineButton><SolidButton onClick={() => onChangeStep(2)} disabled={!mspName.trim()} width="100px" height="32px">Next</SolidButton></>}
          {step === 2 && <><OutlineButton onClick={() => onChangeStep(1)} width="100px" height="32px">Back</OutlineButton><SolidButton onClick={onApplyLicenseKeys} disabled={!completeKeys || isPreconfirming} width="100px" height="32px">{isPreconfirming ? <CircularProgress size={16} color="inherit" /> : "Apply"}</SolidButton><SolidButton onClick={() => onChangeStep(3)} disabled={!canContinue} width="100px" height="32px">Next</SolidButton></>}
          {step === 3 && <><OutlineButton onClick={() => onChangeStep(2)} width="100px" height="32px">Back</OutlineButton><SolidButton onClick={onCreateMsp} width="120px" height="32px">Set Up MSP</SolidButton></>}
        </Box>
      </Box>}
    </ModalContainer>
  );
};

export default ModalSetupMsp;
